import { closeIcon } from "@/assets/icons/close.icon";
import { html, LitElement, nothing, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Role, GetRolesParams, Member } from "@/types";
import { getRoles, updateMemberRoles } from "@/service";

@customElement("sb-manage-roles-form")
export class ManageRolesForm extends LitElement {
  @property({ type: String }) drawerId = "sb-manage-roles";
  @property({ type: String }) organizationId = "";
  @property({ type: Object }) member: Member | null = null;

  @state() private selectedRoles: string[] = [];
  @state() private availableRoles: {
    value: string;
    label: string;
    description?: string;
    icon?: string;
    disabled?: boolean;
  }[] = [];
  @state() private errors: { roles?: string } = {};
  @state() private open = false;
  @state() private isLoading = false;
  @state() private isLoadingRoles = false;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("open-sidedrawer", this._handleOpenDrawer);
    document.addEventListener("close-sidedrawer", this._handleCloseDrawer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("open-sidedrawer", this._handleOpenDrawer);
    document.removeEventListener("close-sidedrawer", this._handleCloseDrawer);
  }

  protected updated(_changedProperties: PropertyValues): void {
    if (_changedProperties.has("member") && this.member) {
      console.log(this.member);
      this.selectedRoles = (this.member.roles || []).map((r) => r.key);
    }
  }

  private _handleOpenDrawer = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    const targetId = detail?.id || detail?.drawer_id;
    if (targetId && targetId !== this.drawerId) return;
    this.open = true;

    if (this.member) {
      this.selectedRoles = (this.member.roles || []).map((r) => r.key);
    }

    if (this.organizationId) {
      this.loadRoles();
    }
  };

  private _handleCloseDrawer = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    const targetId = detail?.id || detail?.drawer_id;
    if (targetId && targetId !== this.drawerId) return;
    this.open = false;
  };

  private _handleDrawerClosed = () => {
    // Sync when drawer closes via overlay or other means
    this.open = false;
  };

  private async loadRoles() {
    try {
      this.isLoadingRoles = true;

      // Fetch roles for the organization
      const params: GetRolesParams = {
        resource_id: this.organizationId,
        resource_type: "organization",
      };

      const response = await getRoles(params);

      // Map API roles to badge selector options
      this.availableRoles = response.data.map((role: Role) => ({
        value: role.key || role.name,
        label: role.name,
        description: role.description || undefined,
        disabled: false,
      }));
    } catch (error) {
      console.error("Failed to load roles:", error);
    } finally {
      this.isLoadingRoles = false;
    }
  }

  private close() {
    this.open = false;
    window.dispatchEvent(
      new CustomEvent("close-sidedrawer", { detail: { id: this.drawerId } }),
    );
  }

  private onRolesChange(roles: string[]) {
    this.selectedRoles = roles;
    this.errors = { ...this.errors, roles: undefined };
  }

  private async handleSave() {
    const err: typeof this.errors = {};

    if (this.selectedRoles.length === 0) {
      err.roles = "Please select at least one role.";
    }

    if (Object.keys(err).length > 0) {
      this.errors = err;
      return;
    }

    if (!this.organizationId) {
      console.error("Organization ID is required");
      return;
    }

    if (!this.member?.user.id) {
      console.error("User ID is required");
      return;
    }

    const payload = {
      roles: this.selectedRoles,
    };

    this.isLoading = true;

    try {
      const response = await updateMemberRoles(
        this.organizationId,
        this.member.user.id,
        payload,
      );
      console.log("Member roles updated successfully:", response);

      this.errors = {};

      window.dispatchEvent(
        new CustomEvent("update-members-role", {
          detail: {
            organizationId: this.organizationId,
            userId: this.member.user.id,
            roles: this.selectedRoles,
            response,
          },
          bubbles: true,
          composed: true,
        }),
      );

      this.close();
    } catch (error) {
      console.error("Failed to update member roles:", error);
      this.errors = {
        ...this.errors,
        roles: "Failed to update member roles. Please try again.",
      };
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    return html`
      <sb-sidedrawer
        id=${this.drawerId}
        side="right"
        inplace
        .open=${this.open}
        @sidedrawer-closed=${this._handleDrawerClosed}
      >
        <div part="container" class="sb-flex sb-flex-col sb-h-full">
          <div>
            <div class="sb-flex sb-justify-between sb-items-center">
              <rtg-typography-h level="h4">Manage Roles</rtg-typography-h>
              <rtg-button
                variant="ghost"
                size="icon-sm"
                part="modal-close-button"
                @click=${this.close}
                ?disabled=${this.isLoading}
              >
                ${closeIcon()}
              </rtg-button>
            </div>
            <rtg-typography-muted>
              Manage roles assigned to users
            </rtg-typography-muted>
          </div>

          <!-- Scrollable content - Flexible middle -->
          <div class="sb-flex-1 sb-overflow-y-auto sb-pl-2 sb-mt-10">
            <div class="sb-flex sb-flex-col sb-gap-6">
              <!-- Email -->
              <div class="sb-flex sb-flex-col sb-gap-2">
                <rtg-label>Email Address</rtg-label>
                <rtg-input
                  .value=${this.member?.user.email || ""}
                  disabled
                ></rtg-input>
              </div>

              <!-- Roles -->
              <div>
                ${this.isLoadingRoles
                  ? html`
                      <div class="sb-flex sb-items-center sb-gap-2">
                        <rtg-spinner size="sm"></rtg-spinner>
                        <rtg-typography-muted
                          >Loading roles...</rtg-typography-muted
                        >
                      </div>
                    `
                  : html`
                      <sb-badge-selector
                        mode="multi"
                        label="Roles"
                        .options=${this.availableRoles}
                        .value=${this.selectedRoles}
                        .onChange=${(roles: string[]) =>
                          this.onRolesChange(roles)}
                        showDescriptions
                        ariaLabel="Select roles"
                        ?disabled=${this.isLoading || this.isLoadingRoles}
                      ></sb-badge-selector>
                    `}
                <rtg-typography-muted class="sb-mt-1">
                  Select one or more roles for this member
                </rtg-typography-muted>
                ${this.errors.roles
                  ? html`<rtg-typography-small part="error"
                      >${this.errors.roles}</rtg-typography-small
                    >`
                  : nothing}
              </div>
            </div>
          </div>

          <!-- Footer buttons - Fixed bottom -->
          <div class="sb-flex sb-justify-end sb-gap-2">
            <rtg-button
              variant="ghost"
              @click=${this.close}
              ?disabled=${this.isLoading}
            >
              Cancel
            </rtg-button>
            <rtg-button
              variant="default"
              ?disabled=${this.selectedRoles.length === 0 ||
              this.isLoading ||
              this.isLoadingRoles}
              @click=${this.handleSave}
            >
              ${this.isLoading
                ? html`
                    <span class="sb-flex sb-items-center sb-gap-2">
                      <rtg-spinner size="sm"></rtg-spinner>
                      Saving...
                    </span>
                  `
                : html`Save`}
            </rtg-button>
          </div>
        </div>
      </sb-sidedrawer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-manage-roles-form": ManageRolesForm;
  }
}
